/**
 * Stage color utility — single source of truth for stage colors.
 * Reads configured hex colors from stageConfig, falls back to sensible defaults.
 */

// Default colors for well-known stages
const DEFAULT_STAGE_COLORS: Record<string, string> = {
  signup: '#9e9e9e',       // grey
  active: '#4caf50',       // green
  at_risk: '#ff9800',      // orange
  churned: '#f44336',      // red
};

// Fallback color for custom stages without a configured color
const FALLBACK_COLOR = '#1976d2'; // blue

export interface StageColorConfig {
  stages?: Array<{ name: string; color?: string }>;
}

/**
 * Get the hex color for a stage, using config first, then defaults.
 */
export function getStageHexColor(
  stageName: string,
  stageConfig?: StageColorConfig | null
): string {
  // Try config first
  if (stageConfig?.stages) {
    const configured = stageConfig.stages.find(s => s.name === stageName);
    if (configured?.color) return configured.color;
  }

  // Fall back to defaults
  return DEFAULT_STAGE_COLORS[stageName] || FALLBACK_COLOR;
}

/**
 * Build a map of stageName -> hexColor for quick lookups.
 */
export function buildStageColorMap(
  stageConfig?: StageColorConfig | null
): Record<string, string> {
  const map: Record<string, string> = { ...DEFAULT_STAGE_COLORS };

  if (stageConfig?.stages) {
    for (const stage of stageConfig.stages) {
      if (stage.color) {
        map[stage.name] = stage.color;
      } else if (!map[stage.name]) {
        map[stage.name] = FALLBACK_COLOR;
      }
    }
  }

  return map;
}
